// components/entities/AuditRenderer.tsx
import React, { memo, useCallback } from 'react';
import clsx from 'clsx';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { Trash2 } from '@/components/ui/lucide-memo';
import { BaseEntityProps, DataAuditDTO, EntityRowAction } from '#types/types';
import {
  resolveAuditActionAvailability,
  resolvePropertyIdFromAudit,
  resolvePropertyNameFromAudit,
} from '#lib/leasing/auditActions';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';

const translations = {
  en: {
    unknownEntity: 'Unknown entity',
    noProcedure: 'Unknown procedure',
    noIssues: 'No issue details',
    verify: 'Verify',
    applyFix: 'Apply fix',
    webSearch: 'Web search',
    createBrandAndUnit: 'Create brand + unit',
    notRealBrand: 'Not a real brand',
    clearData: 'Clear data',
    terminate: 'Mark liquidated',
    detailsTitle: 'Audit details',
    flaggedIssues: 'Flagged issues',
    suggestedFix: 'Suggested fix',
    currentValue: 'Current value',
    notAvailable: 'Not available',
    confirmClearData: 'Clear suspicious field data for this entity?',
    confirmTerminate: 'Mark this brand as liquidated (no hard delete) and close linked audits?',
    confirmNotRealBrand: 'Mark this as not a real brand and close linked audits?',
  },
  fr: {
    unknownEntity: 'Entite inconnue',
    noProcedure: 'Procedure inconnue',
    noIssues: 'Aucun detail',
    verify: 'Verifier',
    applyFix: 'Appliquer fix',
    webSearch: 'Web search',
    createBrandAndUnit: 'Creer brand + unit',
    notRealBrand: 'Pas une vraie marque',
    clearData: 'Vider donnees',
    terminate: 'Marquer liquidee',
    detailsTitle: "Details de l'audit",
    flaggedIssues: 'Problemes detectes',
    suggestedFix: 'Correctif suggere',
    currentValue: 'Valeur actuelle',
    notAvailable: 'Non disponible',
    confirmClearData: 'Vider les donnees suspectes pour cette entite ?',
    confirmTerminate: 'Marquer cette marque comme liquidee (sans suppression) et fermer les audits lies ?',
    confirmNotRealBrand: 'Marquer ceci comme une fausse marque et fermer les audits lies ?',
  },
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const CELL_JUSTIFY: Record<'start' | 'center' | 'end', React.CSSProperties> = {
  start: { justifyContent: 'flex-start' },
  center: { justifyContent: 'center' },
  end: { justifyContent: 'flex-end' },
};

const resolveCellStyle = (column: TableColumnDef) => {
  if (column.id === 'actions' || column.align === 'right') return CELL_JUSTIFY.end;
  if (column.align === 'center') return CELL_JUSTIFY.center;
  return CELL_JUSTIFY.start;
};

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

const scoreClassName = (score: number) => {
  if (score <= 35) return 'text-rose-300';
  if (score <= 60) return 'text-amber-300';
  return 'text-emerald-300';
};

export interface AuditRendererProps extends BaseEntityProps<DataAuditDTO> {
  isExpanded?: boolean;
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<DataAuditDTO>[];
}

export const AuditRenderer = memo(({
  entityId,
  variant = 'row',
  onClick,
  className,
  isExpanded,
  columns,
  rowActions = [],
}: AuditRendererProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const audit = useKernelStore((state) => state.inventory.Audit.registry[entityId]);

  const handleVerify = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    void kernel.current?.resolveAudit(entityId, 'VERIFY');
  }, [entityId, kernel]);

  const handleFix = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    void kernel.current?.resolveAudit(entityId, 'FIX');
  }, [entityId, kernel]);

  const handleClearData = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(t.confirmClearData)) return;
    void kernel.current?.resolveAudit(entityId, 'CLEAR_DATA');
  }, [entityId, kernel, t.confirmClearData]);

  const handleTerminate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(t.confirmTerminate)) return;
    void kernel.current?.resolveAudit(entityId, 'TERMINATE_ENTITY');
  }, [entityId, kernel, t.confirmTerminate]);

  const handleWebSearch = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!audit) return;

    const brandName = (audit.entityName || audit.entityId || '').trim();
    const propertyName = resolvePropertyNameFromAudit(audit) || resolvePropertyIdFromAudit(audit) || '';
    const query = [brandName, propertyName].filter(Boolean).join(' ').trim();
    if (!query) return;

    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
  }, [audit]);

  const handleCreateBrandAndUnit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!audit) return;

    const propertyId = resolvePropertyIdFromAudit(audit);
    void kernel.current?.resolveAudit(entityId, 'CREATE_BRAND_AND_UNIT', propertyId ? { propertyId } : undefined);
  }, [audit, entityId, kernel]);

  const handleNotRealBrand = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(t.confirmNotRealBrand)) return;
    void kernel.current?.resolveAudit(entityId, 'NOT_REAL_BRAND');
  }, [entityId, kernel, t.confirmNotRealBrand]);

  if (!audit) return <EntitySkeleton variant={variant} className={className} />;

  if (variant === 'row') {
    const issues = Array.isArray(audit.flaggedIssues) ? audit.flaggedIssues : [];
    const { canResolve, isPropertyBrandPipelineReview, canTerminate, canApplyFix, canClearData } =
      resolveAuditActionAvailability(audit);

    const cellRenderers: Record<string, React.ReactNode> = {
      entity: (
        <div className="min-w-0 flex flex-col justify-center">
          <div className="truncate font-medium text-white" title={audit.entityName || t.unknownEntity}>
            {audit.entityName || t.unknownEntity}
          </div>
          <div className="truncate text-[11px] text-white/35">
            {audit.entityType} | {audit.entityId}
          </div>
        </div>
      ),
      procedure: (
        <div className="truncate text-white/70" title={audit.procedureName || t.noProcedure}>
          {audit.procedureName || t.noProcedure}
        </div>
      ),
      score: (
        <div className={clsx('font-mono text-sm', scoreClassName(audit.plausibilityScore))}>
          {audit.plausibilityScore}
        </div>
      ),
      issues: (
        <div className="text-white/65">
          {issues.length}
        </div>
      ),
      created: (
        <div className="text-white/55 text-xs">
          {formatDate(audit.createdAt)}
        </div>
      ),
      actions: (
        <div className="flex items-center justify-end gap-2">
          {canResolve && <EntityRowActions entityType="Audit" entity={audit} rowActions={rowActions} />}
          {canResolve && (isPropertyBrandPipelineReview ? (
            <button
              type="button"
              onClick={handleVerify}
              title="Close audit"
              aria-label="Close audit"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-white/15 bg-white/[0.06] text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Trash2 size={12} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleVerify}
              className="h-7 rounded-md border border-white/15 bg-white/[0.06] px-2 text-[10px] text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              {t.verify}
            </button>
          ))}
          {isPropertyBrandPipelineReview && (
            <button
              type="button"
              onClick={handleWebSearch}
              className="h-7 rounded-md border border-sky-500/35 bg-sky-500/10 px-2 text-[10px] text-sky-200 transition-colors hover:bg-sky-500/15"
            >
              {t.webSearch}
            </button>
          )}
          {isPropertyBrandPipelineReview && (
            <button
              type="button"
              onClick={handleCreateBrandAndUnit}
              className="h-7 rounded-md border border-emerald-500/35 bg-emerald-500/10 px-2 text-[10px] text-emerald-200 transition-colors hover:bg-emerald-500/15"
            >
              {t.createBrandAndUnit}
            </button>
          )}
          {isPropertyBrandPipelineReview && (
            <button
              type="button"
              onClick={handleNotRealBrand}
              className="h-7 rounded-md border border-rose-500/35 bg-rose-500/10 px-2 text-[10px] text-rose-200 transition-colors hover:bg-rose-500/15"
            >
              {t.notRealBrand}
            </button>
          )}
          {canApplyFix && (
            <button
              type="button"
              onClick={handleFix}
              className="h-7 rounded-md border border-emerald-500/35 bg-emerald-500/10 px-2 text-[10px] text-emerald-200 transition-colors hover:bg-emerald-500/15"
            >
              {t.applyFix}
            </button>
          )}
          {canClearData && (
            <button
              type="button"
              onClick={handleClearData}
              className="h-7 rounded-md border border-amber-500/35 bg-amber-500/10 px-2 text-[10px] text-amber-200 transition-colors hover:bg-amber-500/15"
            >
              {t.clearData}
            </button>
          )}
          {canTerminate && (
            <button
              type="button"
              onClick={handleTerminate}
              className="h-7 rounded-md border border-rose-500/35 bg-rose-500/10 px-2 text-[10px] text-rose-200 transition-colors hover:bg-rose-500/15"
            >
              {t.terminate}
            </button>
          )}
        </div>
      ),
    };

    const activeColumns = columns || [];

    return (
      <div className="flex w-full flex-none flex-col">
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          className={clsx(
            styles.gridRow,
            styles.bodyRow,
            'group',
            isExpanded && styles.bodyRowExpanded,
            className
          )}
        >
          {activeColumns.map((column) => (
            <div
              key={column.id}
              className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
              style={resolveCellStyle(column)}
            >
              {cellRenderers[column.id] || null}
            </div>
          ))}
        </div>

        {isExpanded && (
          <div className="border-b border-[#2A2A2A] bg-[#151515] px-6 py-4 text-xs text-white/70 space-y-4" onClick={stopPropagation}>
            <div className="text-[11px] text-white/40">{t.detailsTitle}</div>
            <div className="space-y-2">
              <div className="text-white/55">{t.flaggedIssues}</div>
              {issues.length > 0 ? (
                <ul className="list-disc space-y-1 pl-4 text-white/75">
                  {issues.map((issue, index) => (
                    <li key={`${audit.id}-issue-${index}`}>{issue}</li>
                  ))}
                </ul>
              ) : (
                <div className="italic text-white/30">{t.noIssues}</div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded border border-white/10 bg-white/[0.02] p-3">
                <div className="mb-2 text-white/50">{t.suggestedFix}</div>
                <pre className="max-h-[180px] overflow-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-white/75">
                  {audit.suggestedFix ? JSON.stringify(audit.suggestedFix, null, 2) : t.notAvailable}
                </pre>
              </div>
              <div className="rounded border border-white/10 bg-white/[0.02] p-3">
                <div className="mb-2 text-white/50">{t.currentValue}</div>
                <pre className="max-h-[180px] overflow-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-white/75">
                  {audit.currentValue !== undefined && audit.currentValue !== null
                    ? JSON.stringify(audit.currentValue, null, 2)
                    : t.notAvailable}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
});

AuditRenderer.displayName = 'AuditRenderer';
